import React, { useEffect, useRef } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { ReactComponent as SearchIcon } from '../../assets/images/icons/mevo/search.svg';
import { Button } from '../UI-CHAKRA/Button';
import { Input } from '../UI-CHAKRA/Input/Input';
import { InputGroup } from '../UI-CHAKRA/Input/InputGroup';
import { InputRightElement } from '../UI-CHAKRA/Input/InputRightElement';
import { Spinner } from '../UI-CHAKRA/Spinner';
import { FreeSampleCatalogBrand } from '../../interfaces/freeSample';
import FreeSampleHeader from './FreeSampleHeader';

interface FreeSampleLaboratoriesStepProps {
  search: string;
  brands: FreeSampleCatalogBrand[];
  isLoading: boolean;
  onSearchChange: (value: string) => void;
  onSelectBrand: (brand: FreeSampleCatalogBrand) => void;
  onClose: () => void;
  onEventEmission: (eventName: string, extraParams?: Record<string, unknown>) => void;
}

const brandSubtitle = (brand: FreeSampleCatalogBrand): string => {
  const count = brand.items.length;
  if (count === 1) {
    return brand.eligible ? '1 amostra disponível' : '1 amostra · sujeita à aprovação';
  }

  return brand.eligible ? `${count} amostras disponíveis` : `${count} amostras · sujeitas à aprovação`;
};

const FreeSampleLaboratoriesStep: React.FC<FreeSampleLaboratoriesStepProps> = ({
  search,
  brands,
  isLoading,
  onSearchChange,
  onSelectBrand,
  onClose,
  onEventEmission,
}) => {
  const hasEmittedViewEventRef = useRef(false);

  useEffect(() => {
    if (isLoading || brands.length === 0 || hasEmittedViewEventRef.current) return;
    hasEmittedViewEventRef.current = true;
    const laboratories = brands.map((brand) => brand.brand);
    onEventEmission('backoffice_campaign_amostra_gratis_lab_view', { laboratory: laboratories });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, brands]);

  const handleSelectBrand = (brand: FreeSampleCatalogBrand) => {
    onEventEmission('backoffice_campaign_amostra_gratis_lab_click', { laboratory: brand.brand });
    onSelectBrand(brand);
  };

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      <FreeSampleHeader onClose={onClose} />

      <Box flexShrink={0} padding={'8px 20px'}>
        <InputGroup>
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={'Busque pelo laboratório ou produto'}
            variant="outline"
          />
          <InputRightElement>
            <SearchIcon width={'16px'} height={'16px'} />
          </InputRightElement>
        </InputGroup>
      </Box>

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden">
        {isLoading ? (
          <Flex justifyContent={'center'} padding={'40px'}>
            <Spinner color="primary.main" />
          </Flex>
        ) : brands.length === 0 ? (
          <Flex justifyContent={'center'} padding={'40px'}>
            <Text fontSize={'14px'} color={'#6C757D'}>
              Nenhum laboratório encontrado.
            </Text>
          </Flex>
        ) : (
          brands.map((brand) => (
            <Flex
              key={brand.brand}
              flexDirection={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              padding={'12px 20px'}
              borderBottom={'1px solid #E9ECEF'}
              cursor={'pointer'}
              _hover={{ backgroundColor: '#F8F9FA' }}
              onClick={() => handleSelectBrand(brand)}
            >
              <Box>
                <Text fontSize={'16px'} fontWeight={700} color={'#343A40'}>
                  {brand.brand}
                </Text>
                <Text fontSize={'14px'} color={'#6C757D'}>
                  {brandSubtitle(brand)}
                </Text>
              </Box>
              <ChevronRightIcon boxSize={'24px'} color={'primary.main'} />
            </Flex>
          ))
        )}
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onClose}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default FreeSampleLaboratoriesStep;
